import React, { Fragment, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { BiRepost } from "react-icons/bi";
import { FiUserPlus } from "react-icons/fi";
import { MdImage, MdVideoLibrary, MdAudioFile } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";

import {
  BaseLabel,
  StatusBadge,
  ModalDefault,
  TitleSubTitleLabel,
  CircularButton,
  colors,
  MenuListGeneral,
} from "components";
import { useTenant } from "multitenancy/config";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import {
  closeModalAttachmentFile,
  editAttachmentFile,
  setAttachmentList,
} from "store/modules/attachment/actions";

import { LoadSpinner } from "features/LoadSpinner";
import { Page } from "features/RightAudienceComponent/types";

import { ItemDetailLine } from "components/ItemDetailLine";

import {
  hearingFileService as hearingFileServiceLocal,
  hearingFileServiceV2,
} from "services/hearingFile";

import edit from "assets/hearingDetails/edit_attachment.svg";
import fileIcon from "assets/hearingDetails/file.svg";
import DashIcon from "assets/svg/check_drop.svg";
import DownloadIcon from "assets/svg/download-c.svg";
import FileIcon from "assets/svg/FileIcon.svg";
import IconDelete from "assets/svg/IconDelete.svg";
import Notificar from "assets/svg/Notificar.svg";

import { formatDateTime } from "utils/dateTime";
import { LogOperation } from "utils/entities/logOperation";
import { FORMS_ADMIN, FORMS_HEARING } from "utils/entities/permission";
import { convertType } from "utils/format";

import {
  handleCopyMeetingLink,
  listAttendants,
  listItemSchadule,
  showNotify,
} from "./constants";
import { IRightTemplate } from "./interface";
import {
  AttachmentContainer,
  AttachmentIconContainer,
  Container,
  ContainerInfos,
  ContainerText,
  DateItem,
  DatesContainer,
  Description,
  FirstTexts,
  Left,
  LeftContainer,
  Right,
  RightContainer,
  Spacer,
  Title,
  TitleContainer,
} from "./styles";
import { ReadOnlyProvider } from "../../contexts/ReadOnlyContext";

const normalizeFieldName = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const getFieldPrimitiveValue = (field: any) => {
  if (!field || typeof field !== "object" || field instanceof Date) {
    return field;
  }

  return (
    field.value ??
    field.Value ??
    field.fieldValue ??
    field.FieldValue ??
    field.defaultValue ??
    field.DefaultValue
  );
};

const getDynamicFieldValue = (
  audience: any,
  fieldNames: string[],
  customFieldDefinitions?: IRightTemplate["customFields"],
) => {
  const normalizedFieldNames = fieldNames.map(normalizeFieldName);
  const matchesFieldName = (value: unknown) =>
    normalizedFieldNames.includes(normalizeFieldName(value));

  const findFieldValue = (fields: any): any => {
    if (!fields) return undefined;

    if (Array.isArray(fields)) {
      const field = fields.find((item) =>
        [
          item?.name,
          item?.Name,
          item?.key,
          item?.Key,
          item?.fieldName,
          item?.FieldName,
          item?.linkedProperty,
          item?.LinkedProperty,
        ].some(matchesFieldName),
      );

      return getFieldPrimitiveValue(field);
    }

    if (typeof fields === "object") {
      for (const [key, value] of Object.entries(fields)) {
        if (matchesFieldName(key)) {
          return getFieldPrimitiveValue(value);
        }

        if (
          value &&
          typeof value === "object" &&
          [
            (value as any)?.name,
            (value as any)?.Name,
            (value as any)?.key,
            (value as any)?.Key,
            (value as any)?.fieldName,
            (value as any)?.FieldName,
            (value as any)?.linkedProperty,
            (value as any)?.LinkedProperty,
          ].some(matchesFieldName)
        ) {
          return getFieldPrimitiveValue(value);
        }
      }
    }

    return undefined;
  };

  const findCustomFieldValueByDefinition = () => {
    if (!customFieldDefinitions || !Array.isArray(audience?.customFields)) {
      return undefined;
    }

    const customFieldDefinition = customFieldDefinitions.find((field) =>
      [field.name, field.linkedProperty].some(matchesFieldName),
    );

    if (!customFieldDefinition) return undefined;

    const customFieldValue = audience.customFields.find(
      (field: any) =>
        Number(field.hearingCustomFieldId) === Number(customFieldDefinition.id),
    );

    return getFieldPrimitiveValue(customFieldValue);
  };

  return (
    findFieldValue(audience?.dynamicFields ?? audience?.DynamicFields) ??
    findCustomFieldValueByDefinition() ??
    findFieldValue(audience?.customFields ?? audience?.CustomFields)
  );
};

const formatDisplayDate = (value: any) => {
  if (!value) return "-";

  if (value instanceof Date) {
    return formatDateTime.getFullDate(value);
  }

  const rawValue = String(value).trim();
  const dateOnlyMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return formatDateTime.getFullDate(
      new Date(Number(year), Number(month) - 1, Number(day)),
    );
  }

  const parsedDate = new Date(rawValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }

  return formatDateTime.getFullDate(parsedDate);
};

export const RightTemplate: React.FC<IRightTemplate> = ({
  hearingId,
  hearingNumber,
  startDate,
  origin,
  data,
  navigate,
  navigateTo,
  disabled,
  listAttendant,
  onClickRecoding,
  onClick,
  onClickDetach,
  addButton,
  onClickDownloads,
  addNewAttachment,
  onClickDelete,
  onClickValidation,
  onClickEdit,
  setListAttendant,
  onClickNotification,
  listRecord,
  audience,
  customFields,
  record,
  applyReadOnlyOnSearchTerm = false,
  onClickMigrateHearing,
  onClickVincule,
}) => {
  const { t } = useTranslation();
  const { productId } = useTenant();
  const [listSchadule, setListSchadule] = useState<Array<any>>([]);
  const [deleteId, setDeleteId] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { attachmentList } = useSelector((store: any) => store.attachment);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { addToast } = useToast();

  const blockByHearingInProgress = useRef(false);
  const isInqueritoProduct = productId === "inquerito";
  const isAudienceProduct = productId === "audiencias";
  const inqueritoEndDate = getDynamicFieldValue(
    audience,
    ["dataFinal", "ProcedureFinishDate"],
    customFields,
  );

  //#region Permissions
  const {
    read,
    edit,
    hashValidator,
    notify,
    record: hearingRecord,
    hearingCorrection,
    hearingAttendantUnlink,
  } = usePermissions(FORMS_HEARING.HEARING);

  const { create: createAttendant, exclude: excludeAttendant } = usePermissions(
    FORMS_ADMIN.ATTENDANT,
  );

  const {
    edit: editHearingFile,
    exclude: excludeHearingFile,
    export: exportFiles,
    import: importFiles,
  } = usePermissions(FORMS_HEARING.HEARING_FILE);

  //#endregion

  const {
    create: hearingFileCreate,
    edit: hearingFileEdit,
    exclude: hearingFileExclude,
  } = usePermissions(FORMS_HEARING.HEARING_FILE);

  const {
    isAdmin,
    units: allowedUnits,
    userCustomerProfile,
  } = useSelector((s: any) => s.profile);
  const { user } = useSelector((s: any) => s.user);

  const accessibleUnits: number[] = [
    ...(Array.isArray(allowedUnits) ? allowedUnits : []),
    ...(userCustomerProfile?.placeId ? [userCustomerProfile.placeId] : []),
  ].filter(Boolean);
  const audienceUnitId: number | undefined =
    audience?.unitId ?? audience?.placeId;

  const readOnlyByUnit =
    !isAdmin &&
    audienceUnitId != null &&
    !accessibleUnits.includes(audienceUnitId);

  const isReadOnly = Boolean(disabled || readOnlyByUnit);
  const effectiveReadOnly = isReadOnly && applyReadOnlyOnSearchTerm;
  const rawAudienceConfidentiality =
    audience?.isClassified ??
    audience?.IsClassified ??
    audience?.confidential ??
    audience?.Confidential;
  const isConfidentialAudience =
    rawAudienceConfidentiality !== null &&
    rawAudienceConfidentiality !== undefined
      ? Boolean(rawAudienceConfidentiality)
      : Number(
          audience?.confidentialityLevel ?? audience?.ConfidentialityLevel,
        ) > 0;

  const { globalConfigurations } = useSelector(
    (store: any) => store.configurations,
  );
  const recordProvider = globalConfigurations?.find(
    (config: any) => config.name === "RecordProvider",
  )?.value;

  const disabledHearingFileCopy = recordProvider !== "2";
  const disabledCopyAttendantLink = recordProvider !== "2";
  const disabledCopyScheduleLink = recordProvider === "2";

  const canCreateAttendant = Boolean(createAttendant) || Boolean(isAdmin);
  const canUnlinkAttendant =
    !effectiveReadOnly &&
    (audience?.status === "E"
      ? Boolean(isAdmin && hearingAttendantUnlink)
      : Boolean(excludeAttendant));

  const getScheduleActionEnabled = (actionEnabled: boolean) =>
    actionEnabled && !disabled;

  const canCopySchedule = getScheduleActionEnabled(!disabledHearingFileCopy);
  const canDeleteSchedule = getScheduleActionEnabled(
    Boolean(hearingFileExclude),
  );
  const canEditSchedule = getScheduleActionEnabled(Boolean(hearingFileEdit));
  const canRecordSchedule = getScheduleActionEnabled(Boolean(hearingRecord));
  const canShowScheduleCopyLink = getScheduleActionEnabled(
    !disabledCopyScheduleLink,
  );

  const canShowAddParticipantButton = !effectiveReadOnly && canCreateAttendant;

  const handleEditAttachment = (editFile: any) => {
    dispatch(editAttachmentFile(editFile));
  };

  const showEditButton =
    Boolean(edit) &&
    !effectiveReadOnly &&
    !blockByHearingInProgress.current &&
    audience?.status === "A";

  useEffect(() => {
    if (data) {
      setListAttendant(data.hearingAttendants);
      setListSchadule(data.hearingScheduled);

      if (edit) {
        blockByHearingInProgress.current =
          handleHasHearingInProgressOrJoin(audience);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!hearingId) return;
    const fetchAttachments = async () => {
      try {
        const { status, data: dataAttach } =
          await hearingFileServiceLocal.getAllDownloadsWithoutUrlByHearingId(
            hearingId,
          );

        dispatch(setAttachmentList([]));

        if (status === 200) {
          const newList = dataAttach?.data?.filter(
            (a: any) => a.type !== 1 && a.type !== 6,
          );

          dispatch(setAttachmentList(newList));
        }
      } catch {
        dispatch(setAttachmentList([]));
      }
    };

    fetchAttachments();
  }, [hearingId]);

  const showFidedignidade = listRecord?.some(
    (item: any) => Number(item.status) === 13,
  );

  const handleGetUrlByHearingFileId = async (
    hearingFileId: number,
  ): Promise<string | null> => {
    setLoading(true);
    try {
      const response = await hearingFileServiceLocal.getUrlById(hearingFileId);
      const filename = response.body?.filename;
      if (!filename) {
        console.error(
          `getUrlById returned no filename for id=${hearingFileId}`,
          response.body,
        );
        return null;
      }
      return filename;
    } catch (err) {
      console.error("Error fetching hearing file URL:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onClickAta = async () => {
    const ataRecord = record ?? listRecord?.at(0);

    if (!ataRecord || !audience) {
      console.warn("Missing audience or record, aborting navigation.");
      return;
    }

    const audienceVideo =
      ataRecord.filename || (await handleGetUrlByHearingFileId(ataRecord.id));

    const params = new URLSearchParams({
      audienceNumber: audience.procedureNumber,
      hearingFileId: ataRecord.id,
      hearingId: audience.id,
    }).toString();

    navigate(
      `/audiences/fullview?${params}&placeId=${audience.placeId}&audienceVideo=${audienceVideo}&platform=${ataRecord.platform ?? ""}&hiddenFields=marking`,
      {
        state: { audience, page: Page.ATA },
      },
    );
  };

  // console.log("record:", record);
  const listLines = [
    {
      id: "2",
      icon: FileIcon,
      label: t("audiences.ata"),
      disabled: disabled || audience?.status !== "E",
      onClick: record || listRecord?.length ? onClickAta : () => {},
      permission: disabled || audience?.status !== "E" ? false : true,
      hr: false,
    },
    {
      id: "3",
      icon: DownloadIcon,
      label: t("downloads.title"),
      onClick: !disabled && exportFiles ? () => onClickDownloads() : () => {},
      permission: disabled ? false : Boolean(exportFiles),
      hr: true,
    },
    {
      id: "4",
      icon: DashIcon,
      label: t("validation.videoAuthenticityFull"),
      disabled: disabled,
      onClick: !disabled && hashValidator ? onClickValidation : () => {},
      permission: disabled
        ? false
        : Boolean(hashValidator) && Boolean(showFidedignidade),
      hr: true,
    },
    {
      id: "5",
      icon: Notificar,
      label: t("notifications.title"),
      disabled: disabled,
      onClick: !disabled && notify ? onClickNotification : () => {},
      permission: Boolean(notify) && showNotify(audience),
      hr: false,
    },
    {
      id: "6",
      icon: <BiRepost color="#7887A9" />,
      label: t("audiences.migrate"),
      disabled: disabled,
      onClick: hearingCorrection ? onClickMigrateHearing : () => {},
      permission: hearingCorrection && audience?.status === "E",
      hr: isAudienceProduct ? true : false,
    },
    {
      id: "7",
      icon: <FiUserPlus color="#7887A9" />,
      label: t("audiences.serverLink.title"),
      disabled: disabled,
      onClick: hearingCorrection ? onClickVincule : () => {},
      permission:
        userCustomerProfile.confidentialityLevel === 5 &&
        audience.confidentialityLevel == 5 &&
        isAudienceProduct,
      hr: true,
    },
  ];

  const assertCanEdit = () => {
    if (effectiveReadOnly || !edit) {
      addToast({
        type: "warning",
        title: t("common.readOnly"),
        description: t("audiences.messages.noPermission"),
      });
      return false;
    }
    return true;
  };

  const handleClick = () => {
    if (!assertCanEdit()) return;
    onClick?.();
  };

  const handleDeleteAttachment = async () => {
    if (!assertCanEdit()) return;
    if (deleteId) {
      try {
        setShowDeleteModal(false);

        const resp = await hearingFileServiceV2.deleteById(deleteId);

        if (resp?.status === 200) {
          dispatch(closeModalAttachmentFile(true));

          const filtered = (attachmentList ?? []).filter(
            (x: any) => x.hearingFileId !== deleteId,
          );
          dispatch(setAttachmentList(filtered));

          await LogOperation.postLogOperation("HearingFile", 3, deleteId);

          addToast({
            type: "success",
            title: t("attachments.singular"),
            description: t("attachments.deleted"),
          });
        } else {
          addToast({
            type: "error",
            title: t("attachments.deleteError"),
            description: t("attachments.deleteErrorDescription"),
          });
        }
      } catch (error) {
        console.error(error);
        addToast({
          type: "error",
          title: t("common.unexpectedError"),
          description: t("attachments.deleteUnexpectedError"),
        });
      } finally {
      }
    }
  };

  const handleHasHearingInProgressOrJoin = (audience: any) => {
    const hasHearingFileInProgressOrJoin =
      audience.status === "G" || audience.status === "N";

    if (hasHearingFileInProgressOrJoin) {
      return true;
    }
    return false;
  };

  const onClickCopyLinkAttendant = async (item: any) => {
    await navigator.clipboard.writeText(item.link);

    addToast({
      type: "success",
      title: t("common.linkCopied"),
      description: t("common.linkCopiedSuccess"),
    });
  };

  return (
    <ReadOnlyProvider value={effectiveReadOnly}>
      <>
        {read ? (
          <Container data-testid={hearingId} data-readonly={effectiveReadOnly}>
            <ContainerInfos>
              <TitleContainer>
                <Left>
                  <BaseLabel
                    disabled={disabled}
                    fontWeight="700"
                    fontSize="18px"
                    marginLeft="0px"
                  >
                    <p style={{ wordBreak: "break-word" }}>
                      {t("audiences.number", { number: hearingNumber })}
                    </p>
                  </BaseLabel>
                </Left>
                <Right>
                  {showEditButton ? (
                    <CircularButton
                      id="edit"
                      icon="edit"
                      onClick={handleClick}
                    />
                  ) : null}
                </Right>
              </TitleContainer>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "1rem",
                  gap: "1rem",
                }}
              >
                {audience.recordMode === 1 && (
                  <StatusBadge
                    id="video_conferencia"
                    label={t("audiences.recordModeVideoconference")}
                    type="success"
                  />
                )}

                {audience.recordMode === 2 && (
                  <StatusBadge
                    id="video_conferencia"
                    label={t("audiences.recordModeLocal")}
                    type="notified"
                  />
                )}
              </div>
              {effectiveReadOnly ? (
                <div
                  style={{ marginTop: "1rem", fontSize: 12, color: "#8A8A8A" }}
                >
                  {t("common.readOnlyMessage")}
                </div>
              ) : null}
              <FirstTexts>
                <TitleSubTitleLabel
                  childrenTitle={t("common.origin")}
                  fontSizeTitle="12px"
                  fontWeightTitle="400"
                  colorTitle={colors.neutral_gray_40}
                  childrenSubTitle={origin}
                  fontSizeSubTitle="12px"
                  fontWeightSubTitle="700"
                  colorSubTitle={colors.neutral_black}
                  marginBetweenTitleAndSubTitle="0px"
                  marginLeft="0px"
                />
                <DatesContainer>
                  <DateItem>
                    <TitleSubTitleLabel
                      childrenTitle={t("audiences.dateLabel")}
                      fontSizeTitle="12px"
                      fontWeightTitle="400"
                      colorTitle={colors.neutral_gray_40}
                      childrenSubTitle={formatDateTime.getFullDate(startDate)}
                      fontSizeSubTitle="12px"
                      fontWeightSubTitle="700"
                      colorSubTitle={colors.neutral_black}
                      marginBetweenTitleAndSubTitle="0px"
                      marginLeft="0px"
                    />
                  </DateItem>
                  <DateItem>
                    <TitleSubTitleLabel
                      childrenTitle={
                        isInqueritoProduct
                          ? t("audiences.endDate")
                          : t("common.time")
                      }
                      fontSizeTitle="12px"
                      fontWeightTitle="400"
                      colorTitle={colors.neutral_gray_40}
                      childrenSubTitle={
                        isInqueritoProduct
                          ? formatDisplayDate(inqueritoEndDate)
                          : `${formatDateTime.getHour(
                              startDate,
                            )}:${formatDateTime.getMinutes(startDate)}`
                      }
                      fontSizeSubTitle="12px"
                      fontWeightSubTitle="700"
                      colorSubTitle={colors.neutral_black}
                      marginBetweenTitleAndSubTitle="0px"
                      marginLeft="0px"
                    />
                  </DateItem>
                </DatesContainer>
              </FirstTexts>
            </ContainerInfos>
            <Spacer />
            <MenuListGeneral
              title={t("audiences.scheduled")}
              handleButtonClick={() => {}}
              showButton={false}
              disabled={disabled}
              list={
                listItemSchadule({
                  listSchadule,
                  hearingAttendants: listAttendant,
                  navigate,
                  navigateTo,
                  onClickCopy: canCopySchedule
                    ? (e) => handleCopyMeetingLink(e, addToast)
                    : () => {},
                  onClickDelete:
                    onClickDelete && canDeleteSchedule
                      ? onClickDelete
                      : () => {},
                  onClickEdit:
                    onClickEdit && canEditSchedule ? onClickEdit : () => {},
                  onClickRecoding: canRecordSchedule
                    ? onClickRecoding
                    : () => {},
                  disabledCopy: canCopySchedule,
                  disabledDelete: canDeleteSchedule,
                  disabledEdit: canEditSchedule,
                  disabledRecording: canRecordSchedule,
                  disabledCopyLink: !canShowScheduleCopyLink,
                  isProcedure: isInqueritoProduct,
                  isConfidentialAudience,
                  currentUserId: user?.id,
                }) || []
              }
              handleOpen={() => t("audiences.scheduled")}
              emptyMessage={t("audiences.noScheduled")}
            />
            <Spacer />
            <MenuListGeneral
              title={t("participants.title")}
              handleButtonClick={
                canShowAddParticipantButton ? addButton : () => {}
              }
              disabled={disabled}
              showButton={canShowAddParticipantButton}
              list={
                listAttendants(
                  listAttendant,
                  audience,
                  onClickDetach,
                  canUnlinkAttendant,
                  onClickCopyLinkAttendant,
                  disabledCopyAttendantLink || effectiveReadOnly,
                  Boolean(disabled),
                  isInqueritoProduct,
                ) || []
              }
              handleOpen={() => t("participants.title")}
              emptyMessage={t("participants.notFound")}
            />
            <Spacer />
            <MenuListGeneral
              title={t("attachments.title")}
              disabled={disabled}
              handleButtonClick={
                importFiles && !effectiveReadOnly ? addNewAttachment : () => {}
              }
              showButton={Boolean(importFiles && !effectiveReadOnly)}
              list={AttachmentItemList(
                attachmentList,
                setShowDeleteModal,
                setDeleteId,
                editHearingFile && !isReadOnly,
                excludeHearingFile && !isReadOnly,
                t,
                handleEditAttachment,
              )}
              handleOpen={() => {}}
              emptyMessage={t("attachments.notFound")}
            />
            <Spacer />
            {listLines.map((line) => {
              const isDownloadsLine = line.id === "3";
              const lineDisabled =
                (isDownloadsLine ? false : effectiveReadOnly) ||
                Boolean(line.disabled);

              const handleClick =
                isDownloadsLine || !effectiveReadOnly
                  ? (line.onClick as any)
                  : () => {};

              return (
                <ItemDetailLine
                  key={line.id}
                  disabled={lineDisabled}
                  onClick={handleClick}
                  icon={
                    typeof line.icon === "string" ? (
                      <img src={line.icon} alt={line.label} />
                    ) : (
                      line.icon
                    )
                  }
                  label={line.label}
                  permissions={line.permission}
                />
              );
            })}
            <ModalDefault
              openModal={showDeleteModal}
              setOpenModal={() => setShowDeleteModal(false)}
              title={t("attachments.delete")}
              bodyMessageTitle={t("attachments.confirmDelete")}
              bodyMessageSubTitle={t("attachments.cannotUndo")}
              textConfirm={t("buttons.delete")}
              onClickConfirm={() => handleDeleteAttachment()}
              textCancel={t("buttons.cancel")}
              onClickCancel={() => setShowDeleteModal(false)}
            />
          </Container>
        ) : null}
        <LoadSpinner loading={loading} />
      </>
    </ReadOnlyProvider>
  );
};

const imageType = [
  {
    name: "Foto",
    icon: <MdImage color="#7887A9" />,
  },
  {
    name: "Vídeo",
    icon: <MdVideoLibrary color="#7887A9" />,
  },
  {
    name: "Áudio",
    icon: <MdAudioFile color="#7887A9" />,
  },
];

const AttachmentItemList = (
  attachmentList: any[],
  setShowDeleteModal: any,
  setDeleteId: any,
  editHearingFile: boolean | undefined,
  excludeHearingFile: boolean | undefined,
  t: (key: string) => string,
  onEditFile?: (file: any) => void,
): any[] => {
  function formatFileSize(bytes: number) {
    if (bytes < 1024) {
      return bytes + " bytes";
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + " KB";
    } else if (bytes < 1024 * 1024 * 1024) {
      return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    } else if (bytes < 1024 * 1024 * 1024 * 1024) {
      return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
    } else {
      return (bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2) + " TB";
    }
  }

  return attachmentList.map((item: any, index: number) => {
    const deleteIdAttr = `delete-${item.hearingFileId}`;
    const editIdAttr = `edit-${item.hearingFileId}`;

    return (
      <Fragment key={item.filename + index}>
        <AttachmentContainer key={index}>
          <LeftContainer>
            <AttachmentIconContainer>
              {convertType(item?.type) === "Documento" ? (
                <img src={fileIcon} alt="" />
              ) : (
                imageType?.find((x) => x?.name === convertType(item?.type))
                  ?.icon
              )}
            </AttachmentIconContainer>
            <ContainerText>
              <Title>
                {item.filename.length >= 22
                  ? `${item.filename.substring(0, 22)}...`
                  : item.filename}
              </Title>
              <Description>{`${convertType(item?.type)}, ${formatFileSize(
                item.size,
              )}`}</Description>
            </ContainerText>
          </LeftContainer>
          <RightContainer className="container_buttons">
            {excludeHearingFile ? (
              <button
                onClick={() => {
                  setDeleteId(item.hearingFileId);
                  setShowDeleteModal(true);
                }}
                id={deleteIdAttr}
              >
                <img style={{ width: 18 }} src={IconDelete} alt="" />
              </button>
            ) : null}
            {editHearingFile ? (
              <button onClick={() => onEditFile?.(item)} id={editIdAttr}>
                <img src={edit} alt="" />
              </button>
            ) : null}
            <a target="_blank" href={item.url} rel="noreferrer" download />
          </RightContainer>
          <Tooltip
            anchorSelect={`#${deleteIdAttr}`}
            place="top"
            content={t("common.delete")}
          />
          <Tooltip
            anchorSelect={`#${editIdAttr}`}
            place="top"
            content={t("common.edit")}
          />
          <Tooltip
            anchorSelect="#download"
            place="top"
            content={t("common.download")}
          />
        </AttachmentContainer>
      </Fragment>
    );
  });
};
